import { useEffect, useState, useCallback } from "react";
import { SubscriptionState, Subscription } from "../App/useSubscription";
import { useEmitMessage } from "../common/useEmitMessage";
import { useFetch } from '../common/useFetch';

/**
 * 変更可能なプラン一覧作成を担う 
 */
export const useDeliveryPlan = (
  subscription: SubscriptionState,
  setSubscriptions: Function,
  token: string
): UseDeliveryPlan => {

  /**
   * 現在のプランやプラングループ一覧など
   */
  const planGroups = usePlanGroups(token);
  const activePlanGroups = useActivePlanGroups(subscription, planGroups);
  const [modifyPlans, setModifyPlans] = useState<Plan[]>([]);
  const [currentPlan, setCurrentPlan] = useState<Plan>();

  useEffect(() => {
    if (activePlanGroups.length === 0 || !subscription) return;

    const activePlans = activePlanGroups.map(planGroup => planGroup.plans);
    let samePlans: Plan[] = [];

    if (activePlans.length <= 1) {
      samePlans = activePlans.flat();
    } else {
      // 全てのプラングループで重複しているプランを抽出
      let tmp = activePlans.flat();
      while (tmp.length > 0) {
        const target = tmp.shift();
        const check = tmp.filter(plan => isSameBillingInterval(plan, target));

        if (check.length > 0) {
          samePlans = samePlans.concat([target!]);
          tmp = tmp.filter(plan => !isSameBillingInterval(plan, target));
        }
      }
    }
    // 現在の契約プランは除外した上で追加する
    setModifyPlans(
      samePlans.filter(plan => !isSameBillingInterval(plan, subscription))
    );
    setCurrentPlan(
      samePlans.filter(plan => isSameBillingInterval(plan, subscription))[0]
    );

  }, [activePlanGroups, subscription]);

  /**
   * 配送周期更新関連
   */
  const emitMessage = useEmitMessage();
  const failed = () => emitMessage('app-error', 'keep');
  const success = () => emitMessage('app-modify-interval');
  const [state, fetcher] = useFetch<Subscription>('updateSubscriptionLinePlan', token, success, failed);

  // 通信開始・完了時ハンドリング
  useEffect(() => {
    if (state.loading === true)
      emitMessage('app-updating', 'keep');
    if (state.data) 
      setSubscriptions(state.data);
  }, [state.loading, state.data, setSubscriptions]);

  // 更新関数
  const modifyDeliveryPlan = useCallback((nextPlanId: string) => {

    if (!subscription || !planGroups) return;

    // 購入商品一覧
    const variantList = subscription?.subscriptionLines.map(line => {
      return { variantId: line.variantId, lineId: line.lineId };
    });

    const targetPlan = planGroups
      .map(planGroup => planGroup.plans)
      .flat()
      .filter(plan => plan.planId === nextPlanId)[0];
    
    const data = planGroups
      .map(planGroup => {
        const plan = planGroup.plans.find(plan => (
          plan.billingPolicyInterval == targetPlan.billingPolicyInterval &&
          plan.billingPolicyIntervalCount === targetPlan.billingPolicyIntervalCount
        ));
        if (!plan) return;

        const products = planGroup.products?.filter(product => {
          return product.variants.some(variant => {
            return variantList?.some(item => item.variantId === variant.id);
          })
        });
        if (!products || products.length === 0) return;

        return products.map(product => {
          const item = variantList?.find(variant => {
            return product.variants.find(v => v.id === variant.variantId);
          });
          return {
            planId: plan.planId,
            planName: plan.name,
            lineId: item ? item.lineId : '',
            variantId: item ? item.variantId : '',
            pricingPolicyAdjustmentType: plan.pricingPolicyAdjustmentType,
            pricingPolicyAdjustmentValue: plan.pricingPolicyAdjustmentValue
          };
        })
      })
      .filter(d => d)
      .flat();
    
    fetcher({
      subscriptionId: subscription.subscriptionContractId,
      subscriptionLines: data as any, // sorry..
      billingPolicyInterval: targetPlan.billingPolicyInterval,
      billingPolicyIntervalCount: targetPlan.billingPolicyIntervalCount
    });

  }, [planGroups, subscription]);

  return [
    {
      planGroups,
      activePlanGroups,
      currentPlan,
      modifyPlans,
      loadingModifyDeliveryPlan: state.loading
    },
    modifyDeliveryPlan
  ];
}

// 同一配送周期かどうかの判定
const isSameBillingInterval = (a: Subscription | Plan | undefined, b: Subscription | Plan | undefined) => {
  return (
    a?.billingPolicyInterval === b?.billingPolicyInterval &&
    a?.billingPolicyIntervalCount === b?.billingPolicyIntervalCount
  );
};

// 設定されているプラングループ情報を全て取得する
const usePlanGroups = (
  token: string
) => {

  const [state, fetcher] = useFetch<PlanGroupProduct[]>('getProductDetails', token);
  const [planGroups, setPlanGroups] = useState<PlanGroupProduct[]>([]);

  useEffect(() => {
    fetcher({ box: true });
  }, [token]);

  useEffect(() => {
    if (!state.data) return;
    setPlanGroups(state.data);
  }, [state]);

  return planGroups;
}

// 現在の配送商品に紐づいているプラングループを全て取得
const useActivePlanGroups = (
  subscription: SubscriptionState,
  planGroups: PlanGroupProduct[]
) => {

  const [activePlanGroups, setActivePlanGroups] = useState<PlanGroupProduct[]>([]);

  useEffect(() => {
    if (!subscription || planGroups.length === 0) return;

    const variantIds = subscription.subscriptionLines.map(line => line.variantId);

    // 設定されている商品バリエーションを確認
    const filtered = planGroups.filter(planGroup => {
      if (!planGroup.products || planGroup.products.length <= 0) return false;

      // 同一プランが含まれるか確認
      const currentPlan = planGroup.plans.find(plan => {
        return (
          plan.billingPolicyInterval === subscription.billingPolicyInterval &&
          plan.billingPolicyIntervalCount === subscription.billingPolicyIntervalCount
        );
      });
      if (!currentPlan) return false;

      // SubscriptionContractに配送情報がある場合は全バリエーションの中から、
      // SubscriptionContractに配送情報がない場合は「配送が必要な商品です」にチェックのついていないバリエーションの中から、
      // 追加済みバリエーションが存在するか確認
      return planGroup.products.some((product) => {
        return product.variants.some((variant) => variantIds.includes(variant.id));
      })
    });

    setActivePlanGroups([
      ...filtered
    ]);

  }, [subscription, planGroups]);

  return activePlanGroups;
}

// types

type UseDeliveryPlan = [

  {
    planGroups: PlanGroupProduct[],
    activePlanGroups: PlanGroupProduct[],
    currentPlan: Plan | undefined,
    modifyPlans: Plan[],
    loadingModifyDeliveryPlan: boolean
  },
  (nextPlanId: string) => void
]

export type PlanGroupProduct = {
  merchantCode: string;
  name: string;
  planGroupId: string;
  plans: Plan[];
  products?: ProductWithImage[];
  boxes?: Box[];
}

export type Plan = {
  billingPolicyInterval: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';
  billingPolicyIntervalCount: number;
  billingPolicyMaxCycles: number | null;
  billingPolicyMinCycles: number | null;
  firstPricingPolicyAdjustmentType: string | null;
  firstPricingPolicyAdjustmentValue: number | null;
  name: string;
  planId: string;
  pricingPolicyAdjustmentType: string | null;
  pricingPolicyAdjustmentValue: number | null;
}

type ProductWithImage = {
  productId: string;
  title: string;
  images: Image[];
  variants: Variant[];
  onlineStorePreviewUrl: string;
}

type Image = {
  id: string;
  width: number;
  height: number;
  originalSrc: string;
  transformedSrc: string;
};

type Variant = {
  id: string;
  displayName: string;
  title: string;
  sku: string;
  price: string;
  image?: Image;
  requiresShipping: boolean;
  availableForSale: boolean;
};

type Box = {
  id?: number;
  boxId: string;
  title: string;
  description: string;
  pricingType: string;
  planGroupId: string;
  parentProduct: BoxProduct;
  boxVariants: {
    variantId: string;
    quantity: string;
  }[];
  childProducts: BoxProduct[];
  planGroup: {
    id: number;
    name: string;
    plans: Plan[]
  }
};

type BoxProduct = {
  id: number;
  productId: string;
  title: string;
  handle: string;
  variants: {
    id: number;
    variantId: string;
    displayName: string;
  }[];
};
